import {accessTier} from "@/enums/accessTier"

// Same ranks as the backend, spaced so a new tier can be slotted in between
// two existing ones without renumbering the rest.
const ranks: Record<accessTier, number> = {
  [accessTier.LOCKED]: 0,
  [accessTier.FREE]: 20,
  [accessTier.PLUS]: 30,
  [accessTier.CLUB]: 40,
  [accessTier.ENTERPRISE]: 50,
}

type AccountLike = {
  access_level?: unknown
  entitlements?: unknown
  limits?: unknown
} | null | undefined

/**
 * How many of something the plan allows, null for no limit. Lets a control
 * lock itself before someone fills a form the server will refuse.
 */
export function limitOf(account: AccountLike, key: string): number | null {
  const limits = account?.limits

  if (!limits || typeof limits !== 'object') {
    return null
  }

  const limit = (limits as Record<string, unknown>)[key]

  return typeof limit === 'number' ? limit : null
}

/** Out of room? Unknown limits count as room, matching the server. */
export function atLimit(account: AccountLike, key: string, current: number): boolean {
  const limit = limitOf(account, key)

  return limit !== null && current >= limit
}

/**
 * The account's tier, or locked when we cannot tell.
 *
 * The generated model types leave this as `any`, so this is where it gets
 * pinned down. Falling back to locked keeps us on the safe side if an older
 * response arrives without the field.
 */
export function tierOf(account: AccountLike): accessTier {
  const value = account?.access_level

  return typeof value === 'string' && value in ranks
    ? value as accessTier
    : accessTier.LOCKED
}

/**
 * Has this member lost their seat?
 *
 * Typed here rather than in models-override.d.ts: the generator emits appends
 * as `readonly ...?: any` and narrowing them there merges badly. Takes unknown
 * because TS rejects a shape whose only field the real type lacks.
 *
 * Defaults to false, so a payload without it lets someone in.
 */
export function seatSuspended(user: unknown): boolean {
  return (user as { seat_suspended?: unknown } | null | undefined)?.seat_suspended === true
}

/**
 * Does this account reach the given tier? Higher tiers always satisfy a lower
 * requirement, so a club account passes anything asking for plus.
 */
export function tierAtLeast(account: AccountLike, minimum: accessTier): boolean {
  return ranks[tierOf(account)] >= ranks[minimum]
}

/**
 * May this account use the named feature?
 *
 * Reads the entitlements the API sends, which come from the same check the
 * API enforces with, so a locked state here always matches a refusal there.
 * Unknown features are allowed, matching the server.
 */
export function allows(account: AccountLike, feature: string): boolean {
  const entitlements = account?.entitlements

  if (!entitlements || typeof entitlements !== 'object') {
    return true
  }

  return (entitlements as Record<string, boolean>)[feature] !== false
}
